// Значение поля: пусто ли оно, как оно читается человеком, изменилось ли оно.
//
// ЗАЧЕМ ОТДЕЛЬНЫМ ФАЙЛОМ. Эти три вопроса задаются из четырёх разных мест — проверка
// обязательности, пометка «изменено», журнал изменений и подстановка из связанной карточки.
// Ответы обязаны совпадать: если «пусто» для проверки и «пусто» для подстановки считаются
// по-разному, поле будет то краснеть на заполненном значении, то молчать на пустом.
//
// ГЛАВНОЕ ПРАВИЛО, ПОВТОРЁННОЕ ЗДЕСЬ ИЗ МОДЕЛИ ДАННЫХ: пусто — это только пусто.
// Ни `''`, ни `'0'`, ни `0`, ни `false`. Ноль — это ноль (сумма может быть нулевой,
// и «0 ₽» значит «ноль», а не «не заполнено»), снятая галочка — это ответ «нет»,
// а не отсутствие ответа. На `0 == False` в Python зеркало однажды уже потеряло живые нули.

import { fmtDate, fmtMoney } from '../lib/format'

import { ПОДПИСЬ_СОСТОЯНИЯ } from './types'
import type {
  Вариант, Деньги, Значение, Значения, Поле, Состояние, Файл, Ярлык,
} from './types'

// ─────────────────────────── распознавание видов значений ───────────────────────────

export const этоДеньги = (v: unknown): v is Деньги =>
  typeof v === 'object' && v !== null && 'валюта' in v && 'сумма' in v

export const этоЯрлык = (v: unknown): v is Ярлык =>
  typeof v === 'object' && v !== null && 'сущность' in v && 'id' in v

export const этоФайлы = (v: unknown): v is Файл[] =>
  Array.isArray(v) && v.every((f) => typeof f === 'object' && f !== null && 'имя' in f)

// ─────────────────────────── пусто ───────────────────────────

/**
 * Пусто ли значение. Ноль и `false` пустыми НЕ считаются: это ответы.
 *
 * Деньги пусты, когда не названа сумма: валюта по умолчанию стоит всегда, и по ней
 * судить о заполненности нельзя.
 */
export function пусто(v: Значение): boolean {
  if (v === null || v === undefined) return true
  if (typeof v === 'string') return v.trim() === ''
  if (Array.isArray(v)) return v.length === 0
  if (этоДеньги(v)) return v.сумма === null || v.сумма === undefined
  if (этоЯрлык(v)) return !v.id
  return false
}

/** Значение поля по умолчанию: чем заполняется новая карточка. */
export function поУмолчанию(поле: Поле, валюта = 'RUB'): Значение {
  switch (поле.вид) {
    case 'флаг':
      return false
    case 'деньги':
      return { сумма: null, валюта }
    case 'файл':
      return [] as Файл[]
    case 'число':
      return null
    default:
      return ''
  }
}

// ─────────────────────────── равенство ───────────────────────────

/**
 * Равны ли значения. Нужно для пометки «изменено, но не сохранено» и для журнала.
 *
 * Сравнение по значению, а не по ссылке: связь и деньги — объекты, и после каждой
 * перерисовки формы они новые. Сравнение ссылок пометило бы изменённым всё подряд.
 */
export function равны(a: Значение, b: Значение): boolean {
  if (пусто(a) && пусто(b)) return true
  if (этоДеньги(a) && этоДеньги(b)) return a.сумма === b.сумма && a.валюта === b.валюта
  if (этоЯрлык(a) && этоЯрлык(b)) return a.сущность === b.сущность && a.id === b.id
  if (этоФайлы(a) && этоФайлы(b)) {
    if (a.length !== b.length) return false
    return a.every((f, i) => f.id === b[i].id)
  }
  return a === b
}

// ─────────────────────────── варианты списка ───────────────────────────

/**
 * Какие варианты у поля-списка ПРЯМО СЕЙЧАС.
 *
 * ЗАЧЕМ ОТДЕЛЬНОЙ ФУНКЦИЕЙ. Набор бывает зависимым: стадия сужается по воронке
 * (`Поле.вариантыПо`), потому что код стадии уникален внутри воронки, а не вообще. Спросить
 * об этом должны все трое одинаково — выпадающий список, проверка значения и подпись в
 * журнале, — иначе форма покажет одно, проверка потребует другого, а журнал напишет третье.
 *
 * Поле-хозяин не заполнено или его значение не встречается в наборах — отдаём плоский
 * список. Это не поблажка: до выбора воронки сузить не по чему, а пустой список читался бы
 * как «выбирать нечего».
 */
export function вариантыПоля(поле: Поле, все?: Значения): Вариант[] {
  const по = поле.вариантыПо
  if (по && все) {
    const хозяин = все[по.поле]
    if (!пусто(хозяин)) {
      const набор = по.наборы[String(хозяин)]
      if (набор) return набор
    }
  }
  return поле.варианты ?? []
}

// ─────────────────────────── текст для человека ───────────────────────────

const ЧИСЛО_РУ = (n: number) => n.toLocaleString('ru-RU', { maximumFractionDigits: 3 })

/**
 * Как значение читается человеком: в журнале изменений, в шапке, в таблице обратных связей.
 *
 * Пустое — прочерк, и это ОТВЕТ «не заполнено», а не отсутствие ответа: журнал обязан
 * показывать «из „—“ в „Иванов“», иначе первое заполнение поля выглядит как его пропажа.
 */
export function текстЗначения(поле: Поле, v: Значение, все?: Значения): string {
  if (пусто(v)) return '—'
  switch (поле.вид) {
    case 'деньги': {
      const д = v as Деньги
      return fmtMoney(д.сумма, д.валюта)
    }
    case 'дата':
      return fmtDate(String(v))
    case 'флаг':
      return v ? 'Да' : 'Нет'
    case 'список': {
      const вар = вариантыПоля(поле, все).find((x) => x.код === String(v))
      // Кода нет в справочнике — показываем сам код, а не прочерк. Прочерк соврал бы
      // «не заполнено» на заполненном поле, а код хотя бы можно найти в базе.
      return вар ? вар.подпись : String(v)
    }
    case 'связь':
      return (v as Ярлык).название
    case 'файл':
      return (v as Файл[]).map((f) => f.имя).join(', ')
    case 'число':
      return typeof v === 'number' ? ЧИСЛО_РУ(v) : String(v)
    default:
      return String(v)
  }
}

/** Подпись состояния карточки. */
export const текстСостояния = (с: Состояние): string => ПОДПИСЬ_СОСТОЯНИЯ[с] ?? с

// ─────────────────────────── чтение значений ───────────────────────────

export function строка(з: Значения, код: string): string {
  const v = з[код]
  return пусто(v) ? '' : String(v)
}

export function число(з: Значения, код: string): number | null {
  const v = з[код]
  if (typeof v === 'number') return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.replace(/\s/g, '').replace(',', '.'))
    return Number.isNaN(n) ? null : n
  }
  return null
}

export function деньги(з: Значения, код: string): Деньги {
  const v = з[код]
  return этоДеньги(v) ? v : { сумма: null, валюта: 'RUB' }
}

export function ярлык(з: Значения, код: string): Ярлык | null {
  const v = з[код]
  return этоЯрлык(v) && v.id ? v : null
}

export function файлы(з: Значения, код: string): Файл[] {
  const v = з[код]
  return этоФайлы(v) ? v : []
}
